"use client";

import { ArrowRight, LayoutDashboard, LogOut } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import React from "react";
import { localizedPath } from "../i18n/paths";
import { useAuth } from "./auth-client";
import { HeaderNavigationLink } from "./header-navigation";
import { ProfileAvatar } from "./profile-avatar";

function LoginSignupContent({ label }: { readonly label: string }) {
  return (
    <>
      <span className="account-cta-label">{label}</span>
      <span className="account-cta-arrow" aria-hidden="true">
        <ArrowRight className="size-3.5 stroke-[1.6] rtl:rotate-180" />
      </span>
    </>
  );
}

export function AccountAction() {
  const auth = useAuth();
  const locale = useLocale();
  const t = useTranslations("Auth");
  const tCommon = useTranslations("Common");
  const accountMenuRef = React.useRef<HTMLDetailsElement>(null);

  React.useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const accountMenu = accountMenuRef.current;
      if (
        !accountMenu?.open ||
        !(event.target instanceof Node) ||
        accountMenu.contains(event.target)
      ) {
        return;
      }

      accountMenu.open = false;
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  if (auth.status === "loading" || auth.status === "unavailable") {
    return (
      <span
        className="hidden h-11 w-28 animate-pulse rounded-sm bg-subtle sm:block"
        aria-label={t("loading")}
      />
    );
  }

  if (auth.status === "anonymous") {
    return (
      <HeaderNavigationLink
        href="/login"
        className="account-cta hidden sm:inline-flex"
      >
        <LoginSignupContent label={t("loginSignup")} />
      </HeaderNavigationLink>
    );
  }

  return (
    <details ref={accountMenuRef} className="group relative hidden sm:block">
      <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-md border border-border-default bg-surface px-4 text-sm font-semibold text-primary transition-colors marker:content-none hover:bg-action-secondary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">
        <ProfileAvatar
          className="size-7"
          profilePictureUrl={auth.account.profilePictureUrl}
        />
        <span className="max-w-36 truncate">
          {auth.account.displayName ?? t("account")}
        </span>
      </summary>
      <div className="absolute end-0 top-[calc(100%+0.5rem)] z-50 min-w-56 rounded-lg border border-border-subtle bg-surface p-2 shadow-elevation-2">
        <HeaderNavigationLink
          href="/dashboard"
          className="flex min-h-11 items-center gap-2 rounded-sm px-3 text-sm font-semibold text-primary hover:bg-subtle"
        >
          <LayoutDashboard className="size-4 text-brand" aria-hidden="true" />
          {tCommon("dashboard")}
        </HeaderNavigationLink>
        <HeaderNavigationLink
          href="/account"
          className="flex min-h-11 items-center rounded-sm px-3 text-sm font-semibold text-primary hover:bg-subtle"
        >
          {t("manageAccount")}
        </HeaderNavigationLink>
        {auth.account.platformRole === "ADMIN" ? (
          <HeaderNavigationLink
            href="/admin"
            className="flex min-h-11 items-center rounded-sm px-3 text-sm font-semibold text-primary hover:bg-subtle"
          >
            {t("adminDashboard")}
          </HeaderNavigationLink>
        ) : null}
        <button
          type="button"
          onClick={() => {
            void auth.logout(localizedPath(locale, "/"));
          }}
          className="flex min-h-11 w-full items-center gap-2 rounded-sm px-3 text-start text-sm font-semibold text-danger hover:bg-danger-subtle"
        >
          <LogOut className="size-4" aria-hidden="true" />
          {t("logout")}
        </button>
      </div>
    </details>
  );
}

export function MobileAccountLinks() {
  const auth = useAuth();
  const locale = useLocale();
  const t = useTranslations("Auth");
  const tCommon = useTranslations("Common");
  if (auth.status === "loading" || auth.status === "unavailable") {
    return (
      <span role="status" className="mt-3 text-sm text-secondary">
        {t("loading")}
      </span>
    );
  }
  if (auth.status !== "authenticated") {
    return (
      <HeaderNavigationLink
        href="/login"
        className="account-cta account-cta-mobile mt-3 flex"
      >
        <LoginSignupContent label={t("loginSignup")} />
      </HeaderNavigationLink>
    );
  }
  return (
    <>
      <HeaderNavigationLink
        href="/dashboard"
        className="mt-2 flex min-h-11 items-center rounded-sm border border-brand bg-brand-subtle px-3 text-sm font-bold text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
      >
        {tCommon("dashboard")}
      </HeaderNavigationLink>
      <HeaderNavigationLink
        href="/account"
        className="mt-2 flex min-h-11 items-center rounded-sm border border-border-default px-3 text-sm font-semibold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
      >
        {auth.account.displayName ?? t("account")}
      </HeaderNavigationLink>
      {auth.account.platformRole === "ADMIN" ? (
        <HeaderNavigationLink
          href="/admin"
          className="flex min-h-11 items-center rounded-sm px-3 text-sm font-semibold text-primary hover:bg-subtle"
        >
          {t("adminDashboard")}
        </HeaderNavigationLink>
      ) : null}
      <button
        type="button"
        onClick={() => {
          void auth.logout(localizedPath(locale, "/"));
        }}
        className="flex min-h-11 w-full items-center gap-2 rounded-sm px-3 text-start text-sm font-semibold text-danger hover:bg-danger-subtle"
      >
        <LogOut className="size-4" aria-hidden="true" />
        {t("logout")}
      </button>
    </>
  );
}
